import {PDFDocument, PDFFont, PDFPage, rgb, StandardFonts} from "pdf-lib";
import {DateTime} from "luxon";
import {i18n} from "i18next";
import {AgenciesLogosValuesProps} from "../redux/reducer/user";
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray, TRAVEL_OPTION_TYPE} from "../types-enums/activity";
import {isActivityCarBusState, isActivityFlightState, isActivityHotelState} from "../utils/check/type-guards";
import {
    generateCustomerFlightRouteWithMetaDataString,
    generatFlightCalculationImprovementsString
} from "../utils/string/flight";
import {generateCustomerHotelLocationString} from "../utils/string/hotel";
import {CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL} from "../api/model/common";
import {
    Co2KgEPerActivityProps,
    Co2KgEPerTravelOptionProps,
    getCo2KgEForActivityType,
    getCo2KgEForAllTravelOptionTypes,
    getTotalCo2KgEForJourneyViaSumOfDays
} from "../utils/getter/co2-getter";
import {
    capitalizeFirstLetter,
    formatCo2ETotalString,
    formatDateString,
    generateCo2KgEForActivityString,
    generatePersonCountString,
    WhereToDisplayActivityString
} from "../utils/string/common";
import {roundCo2E} from "../utils/math/round-values";
import {hasExactDates} from "../utils/getter/journey";
import {JourneyState} from "../redux/reducer/journey";
import {getBackendCalculationStatus} from "../utils/getter/emissions";
import {
    generateCarBusCalculationImprovementsString,
    generateCustomerCarBusInformationString
} from "../utils/string/car-bus";

export const primaryColor = rgb(0.058823529411764705, 0.3568627450980392, 0.5333333333333333)
export const secondaryColor = rgb(0.6078431372549019, 0.7607843137254902, 0.23529411764705882)
export const lightGrey = rgb(0.9019607843137255, 0.8980392156862745, 0.8980392156862745)
export const darkGrey = rgb(0.615686274509804, 0.615686274509804, 0.6196078431372549)
export const black = rgb(0, 0, 0)

export interface LogoProps {
    pathToLogo: ArrayBuffer | null,
    fileType: AgenciesLogosValuesProps["fileType"] | null
}

const FONT_URL = 'https://pdf-lib.js.org/assets/ubuntu/Ubuntu-R.ttf'
export const SPACER_TO_CO2_KG_E = 30
const MAX_JOURNEY_TITLE_LENGTH = 80
const MAX_JOURNEY_DESCRIPTION_LENGTH = 100
const JOURNEY_TITLE_DESCRIPTION_REPLACER = "..."

/**
 * Computes the positions for the first row, including the title's Y position,
 * the body's Y position, and the remaining height available for content.
 *
 * @param {number} pageHeight - The total height of the page.
 * @param {number} totalHeaderHeight - The cumulative height of the header components.
 * @param {number} marginFromTravelComponentTitleToBody - The vertical margin between the title
 *        of the travel component and its body content.
 * @param {number} heightForContent - The total height available for content after
 *        accounting for headers and margins.
 * @returns {Object} An object containing the calculated properties:
 *          - heightLeftOver: The remaining height available for content.
 *          - firstRowTitleYPosition: The Y-axis position for the first title.
 *          - firstBodyYPosition: The Y-axis position for the first body.
 */
export const calculateFirstRowPosition = (
    pageHeight: number,
    totalHeaderHeight: number,
    marginFromTravelComponentTitleToBody: number,
    heightForContent: number,
    journeyTitleAndDescriptionHeight?: number,
) => {
    const originalDayTitleYPosition = calculateBeginningOfContent(pageHeight, totalHeaderHeight)
    let firstRowTitleYPosition = originalDayTitleYPosition
    let firstBodyYPosition = firstRowTitleYPosition - marginFromTravelComponentTitleToBody
    if (typeof journeyTitleAndDescriptionHeight !== 'undefined') {
        firstRowTitleYPosition -= journeyTitleAndDescriptionHeight
        firstBodyYPosition -= journeyTitleAndDescriptionHeight
    }

    const heightLeftOver = heightForContent
    return {heightLeftOver, firstRowTitleYPosition, firstBodyYPosition, originalDayTitleYPosition}
}

/**
 * Asynchronously sets up fonts for a PDF document.
 *
 * This function embeds a bold Helvetica font and a normal font from
 * a specified URL into a PDFDocument instance. It retrieves the normal
 * font as an array buffer from the URL and embeds it with subsetting.
 *
 * @param {PDFDocument} pdfDoc - The PDF document instance where fonts will be embedded.
 * @returns {Promise<{fontBold: PDFFont, fontNormal: PDFFont}>} A promise that resolves to an object containing the embedded bold and normal fonts.
 * @throws {Error} If the fonts cannot be fetched or embedded.
 */
export const setupPdfFonts = async (pdfDoc: PDFDocument,): Promise<{ fontBold: PDFFont; fontNormal: PDFFont; }> => {
    const fontNormalBytes = await fetch(FONT_URL).then((res) => res.arrayBuffer())
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
    const fontNormal = await pdfDoc.embedFont(fontNormalBytes, {subset: true})
    return {
        fontBold,
        fontNormal
    }
}

export const setUpPdfPageMargins = () => ({pageMarginLeft: 50, pageMarginRight: 50})

/**
 * Configures and returns general PDF properties, such as dimensions and formatting values,
 * based on provided header and page height.
 *
 * @param {number} headerHeight - The height of the header in the PDF document.
 * @param {number} pageHeight - The total height of the PDF page.
 * @returns {Object} An object containing various properties for PDF layout and styling,
 * such as footer height, font sizes, line heights, margins, and dimensions for content areas.
 */
export const setupGeneralPdfProps = (
    headerHeight: number,
    pageHeight: number,
) => {
    const footerHeight = headerHeight
    const titleFontSize = 12
    const marginFromBodyToHeader = 30
    const totalHeaderHeightWithMargin = headerHeight + marginFromBodyToHeader
    const heightForContent = pageHeight - totalHeaderHeightWithMargin - footerHeight
    const bodyFontSize = 9
    return {
        footerHeight,
        titleFontSize,
        marginFromBodyToTitle: titleFontSize + 2,
        rowGapForTravelComponentsPdf: 10,
        rowGapForJourneyPdf: 15,
        bodyFontSize,
        bodyLineHeight: 12,
        titleLineHeight: 14,
        totalHeaderHeightWithMargin,
        heightForContent,
        marginFromActivitiesToTravelOptionType: bodyFontSize + 4,
        marginJourneyPropsToActivities: 10
    }
}

/**
 * Asynchronously sets the header on a PDF page with specified content and styles.
 *
 * @param {PDFPage} page - The PDF page object where the header will be drawn.
 * @param {number} pageHeight - The height of the PDF page in points.
 * @param {number} pageWidth - The width of the PDF page in points.
 * @param {PDFFont} fontNormal - The standard font to be used for non-bold text.
 * @param {PDFFont} fontBold - The font to be used for bold text.
 * @param {number} pageMarginLeft - The left page margin in points for aligning text.
 * @param {number} pageMarginRight - The right page margin in points for aligning text.
 * @param {i18n} i18n - An instance of the internationalization object used for formatting dates.
 * @param {PDFDocument} pdfDoc - The PDF document object to which the page belongs.
 * @param {string} employeeName - The name of the employee to be displayed in the header.
 * @param {string} agencyName - The name of the agency to be displayed prominently in the header.
 * @param {string} agencyAddress - The address of the agency to be displayed in the header.
 * @param {LogoProps} logo - An object containing properties related to the logo, such as its file path and type.
 * @param {string} email - The email address of the employee to include in the header.
 * @param {string} completePhoneNumber - The complete phone number of the employee to include in the header.
 *
 * @returns {Promise<number>} A promise that resolves to the total header height in points, including title, address, employee information, and line.
 */
export const setHeader = async (
    page: PDFPage,
    pageHeight: number,
    pageWidth: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    pageMarginLeft: number,
    pageMarginRight: number,
    i18n: i18n,
    pdfDoc: PDFDocument,
    employeeName: string,
    agencyName: string,
    agencyAddress: string,
    logo: LogoProps,
    email: string,
    completePhoneNumber: string,
): Promise<number> => {
    const fontSizeAgencyName = 15
    const titleHeight = 4.5 * fontSizeAgencyName
    const titleYPosition = pageHeight - titleHeight
    page.drawText(agencyName, {
        x: pageMarginLeft,
        y: titleYPosition,
        size: fontSizeAgencyName,
        font: fontBold,
        color: black,
    })

    const fontSizeAddress = 12
    const addressHeight = 1.2 * fontSizeAddress
    const addressYPosition = titleYPosition - addressHeight
    page.drawText(agencyAddress, {
        x: pageMarginLeft,
        y: addressYPosition,
        size: fontSizeAddress,
        font: fontNormal,
        color: black,
    })


    const currentDate = formatDateString(DateTime.now(), i18n.resolvedLanguage, false)
    const fontSizeDate = 10
    const currentDateWidth = fontNormal.widthOfTextAtSize(currentDate, fontSizeDate);
    page.drawText(currentDate, {
        x: pageWidth - pageMarginRight - currentDateWidth,
        y: titleYPosition,
        size: fontSizeDate,
        font: fontNormal,
        color: black,
    })


    const fontSizeEmployeeName = 10
    const employeeHeight = 1.5 * fontSizeEmployeeName
    const employeeNameYPosition = addressYPosition - employeeHeight
    page.drawText(`${employeeName}, ${email}, ${completePhoneNumber}`, {
        x: pageMarginLeft,
        y: employeeNameYPosition,
        size: fontSizeEmployeeName,
        font: fontNormal,
        color: darkGrey,
    })

    const lineXPosition = 50;
    const lineStart = pageWidth - lineXPosition
    const lineThickness = 1.5
    const lineHeight = 5 * lineThickness
    const lineYPosition = employeeNameYPosition - lineHeight
    page.drawLine({
        start: {
            x: lineXPosition,
            y: lineYPosition
        },
        end: {
            x: lineStart,
            y: lineYPosition
        },
        thickness: lineThickness,
        color: black,
    })

    if (logo.pathToLogo && logo.fileType) await addLogo(page, pageHeight, pageWidth, pdfDoc, pageMarginRight, employeeNameYPosition, {
        pathToLogo: logo.pathToLogo,
        fileType: logo.fileType
    })
    return titleHeight + addressHeight + employeeHeight + lineHeight
}

/**
 * Calculates the optimal width and height of an image to fit within a given box
 * while maintaining the aspect ratio of the image.
 *
 * @param {number} imageWidth - The original width of the image.
 * @param {number} imageHeight - The original height of the image.
 * @param {number} boxWidth - The width of the bounding box.
 * @param {number} boxHeight - The height of the bounding box.
 * @returns {Object} An object containing the calculated width and height of the image.
 * @returns {number} width - The calculated width of the resized image.
 * @returns {number} height - The calculated height of the resized image.
 */
const calculateImageSize = (
    imageWidth: number,
    imageHeight: number,
    boxWidth: number,
    boxHeight: number
): { width: number; height: number } => {
    const imageAspectRatio = imageWidth / imageHeight;
    const boxAspectRatio = boxWidth / boxHeight;

    let newWidth: number;
    let newHeight: number;

    if (imageAspectRatio > boxAspectRatio) {
        newWidth = boxWidth;
        newHeight = boxWidth / imageAspectRatio;
    } else {
        newWidth = boxHeight * imageAspectRatio;
        newHeight = boxHeight;
    }

    return {
        width: newWidth,
        height: newHeight,
    };
}

/**
 * Adds a logo to a specified PDF page.
 *
 * @param {PDFPage} page - The PDF page where the logo will be added.
 * @param {number} pageHeight - The height of the PDF page.
 * @param {number} pageWidth - The width of the PDF page.
 * @param {PDFDocument} pdfDoc - The PDF document to which the page belongs.
 * @param {number} pageMarginRight - The right margin of the page, used to position the logo.
 * @param {number} logoPosition - The vertical position (y-axis) where the logo should be placed.
 * @param {LogoProps} logo - An object containing the logo's file path and type.
 * @returns {Promise<void>} A promise that resolves after the logo has been added to the PDF page.
 *
 * @throws {Error} If the file type of the logo is not supported or the path to the logo is not defined.
 *
 * @description
 * The `addLogo` function embeds a logo image onto the specified PDF page. The supported file types for the logo are JPEG and PNG.
 * It calculates the logo's size while maintaining its aspect ratio and then draws the image at the specified position on the page.
 */
export const addLogo = async (
    page: PDFPage,
    pageHeight: number,
    pageWidth: number,
    pdfDoc: PDFDocument,
    pageMarginRight: number,
    logoPosition: number,
    logo: LogoProps
): Promise<void> => {
    const {pathToLogo, fileType} = logo
    const desiredLogoWidth = 100
    const desiredLogoHeight = 25
    let xPosition = page.getWidth() - pageMarginRight
    let yPosition = logoPosition
    if (fileType && pathToLogo) {
        /// https://jsfiddle.net/Hopding/bcya43ju/5/

        if (fileType === "jpg" || fileType === "jpeg") {
            const jpgImageBytes = pathToLogo
            const jpgImage = await pdfDoc.embedJpg(jpgImageBytes)
            const {width, height} = jpgImage
            const calculatedImageSize = calculateImageSize(width, height, desiredLogoWidth, desiredLogoHeight)

            page.drawImage(jpgImage, {
                x: xPosition - calculatedImageSize.width,
                y: yPosition,
                width: calculatedImageSize.width,
                height: calculatedImageSize.height,
            })
        }

        if (fileType === "png") {
            const pngImageBytes = pathToLogo
            const pngImage = await pdfDoc.embedPng(pngImageBytes)
            const {width, height} = pngImage
            const calculatedImageSize = calculateImageSize(width, height, desiredLogoWidth, desiredLogoHeight)
            page.drawImage(pngImage, {
                x: xPosition - calculatedImageSize.width,
                y: yPosition,
                width: calculatedImageSize.width,
                height: calculatedImageSize.height,
            })
        }
    }
    return Promise.resolve()
}

/**
 * Sets the footer content on a PDF page.
 *
 * @param {PDFPage} page - The PDF page object where the footer will be drawn.
 * @param {number} pageHeight - The height of the page in points.
 * @param {number} pageWidth - The width of the page in points.
 * @param {PDFFont} fontNormal - The font to be used for normal text in the footer.
 * @param {PDFFont} fontBold - The font to be used for bold text in the footer.
 * @param {number} footerYStartPosition - The Y-coordinate where the footer starts.
 * @param {number} pageMarginLeft - The left margin of the page in points.
 * @param {number} pageMarginRight - The right margin of the page in points.
 * @param {i18n} i18n - The internationalization object, used for text translations.
 */
export const setFooter = (
    page: PDFPage,
    pageHeight: number,
    pageWidth: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    footerYStartPosition: number,
    pageMarginLeft: number,
    pageMarginRight: number,
    i18n: i18n,
) => {
    const {t} = i18n
    const lineStart = pageWidth - pageMarginLeft
    const lineThickness = 1
    const lineHeight = 5 * lineThickness
    const lineYPosition = footerYStartPosition - lineHeight
    const fontSizeInfo = 8
    const textBox = pageWidth - pageMarginRight - pageMarginLeft
    const lineHeightWrappedText = 10
    page.drawLine({
        start: {
            x: pageMarginLeft,
            y: lineYPosition
        },
        end: {
            x: lineStart,
            y: lineYPosition
        },
        thickness: lineThickness,
        color: darkGrey,
    })

    const text1 = wrapText(t("PDF.TRAVEL_COMPONENTS.FOOTER.REDUCE_EMISSIONS"), textBox, fontNormal, fontSizeInfo, " ")
    const text1CountTextLines = text1.countTextLines
    const text1Height = (text1CountTextLines * lineHeightWrappedText) - fontSizeInfo
    const marginToLine = 10
    const text1YPosition = lineYPosition - text1Height - marginToLine
    page.drawText(text1.text, {
        x: pageMarginLeft,
        y: text1YPosition,
        size: fontSizeInfo,
        font: fontNormal,
        lineHeight: lineHeightWrappedText,
        color: darkGrey,
    })

    const text2 = wrapText(t("GENERAL.CO2_E_EXPLANATION"), textBox, fontNormal, fontSizeInfo, " ")
    const text2CountTextLines = text2.countTextLines
    const marginToText1 = text1CountTextLines * 10
    const text2YPosition = text1YPosition - fontSizeInfo - marginToText1
    page.drawText(text2.text, {
        x: pageMarginLeft,
        y: text2YPosition,
        size: fontSizeInfo,
        lineHeight: lineHeightWrappedText,
        font: fontNormal,
        color: darkGrey,
    })

    const marginToText2 = text2CountTextLines * 10
    const text3YPosition = text2YPosition - fontSizeInfo - marginToText2
    page.drawText(t("PDF.TRAVEL_COMPONENTS.FOOTER.DISCLAIMER"), {
        x: pageMarginLeft,
        y: text3YPosition,
        size: fontSizeInfo,
        font: fontNormal,
        color: darkGrey,
    })
}

/**
 * This function checks of a given text fits the page of PDF within a specified page width based on the font and font size.
 * If not, it will wrap the text by inserting '\n' until it fits in the page. It returns the text with inserted '\n' and
 * the count of the lines the text occupies. Note, this only works with 'continuous' text. If the input text has already
 * '\n' in it, this will mess up
 * 1) checking if the text fits on the page and
 * 2) the returned count of the text lines
 *
 * @param {string} text - The input text to be wrapped.
 * @param {number} pageWidth - The maximum width of text for a single line.
 * @param {PDFFont} font - The font to be used for measuring the text width.
 * @param {number} fontSize - The size of the font to be used.
 * @param {string} separator - The character used to separate words (e.g., space).
 * @returns {{text: string, countTextLines: number}} An object containing the wrapped text (`text`) with lines separated by newline characters, and the total number of resulting text lines (`countTextLines`).
 */
export const wrapText = (text: string, pageWidth: number, font: PDFFont, fontSize: number, separator: string): {
    text: string;
    countTextLines: number;
} => {
    const words = text.split(separator);
    let line = '';
    let result = '';
    let countTextLines = 1
    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const textWidth = font.widthOfTextAtSize(testLine, fontSize);
        if (textWidth > pageWidth) {
            result += line + '\n';
            line = words[n] + ' ';
            countTextLines += 1
        } else {
            line = testLine;
        }
    }
    result += line;
    return {text: result, countTextLines};
}

/**
 * Adds activity-specific text to the provided text result object by wrapping the text within a specified width.
 *
 * @param {string} activityStringToAdd - The activity text to be added.
 * @param {number} textBlockMaxWidth - The maximum width allowed for the text block.
 * @param {PDFFont} fontFamily - The font family used for rendering the text.
 * @param {number} fontSize - The font size to be used for the text.
 * @param {string} separator - The character used to separate text during wrapping.
 * @param {TextResultProps} textResult - The text result object to which the formatted text and line count will be added.
 * @returns {number} The number of lines in the wrapped text.
 */
const addActivitySpecificTextToTextResults = (
    activityStringToAdd: string,
    textBlockMaxWidth: number,
    fontFamily: PDFFont,
    fontSize: number,
    separator: string,
    textResult: TextResultProps
) => {
    const result = wrapText(activityStringToAdd, textBlockMaxWidth, fontFamily, fontSize, separator)
    const {text, countTextLines} = result
    textResult.push({
        activityText: `${text}\n`,
        linesCount: countTextLines
    })
    return countTextLines
}

type TextResultProps = {
    activityText: string, linesCount: number
}[]

/**
 * Generates formatted text and line count for an activity based on its type and properties.
 * @param {AllActivityTypes} activity - The activity object containing details specific to the activity type.
 * @param {i18n} i18n - Internationalization object used for localizing text based on the current language.
 * @param {number} textBlockMaxWidth - The maximum width of a text block used for fitting the text accordingly.
 * @param {PDFFont} fontFamily - The font family used to render the text.
 * @param {number} fontSize - The size of the font used for rendering text lines.
 * @param {string} separator - A string used to separate parts of the text content.
 * @param {boolean} isJourney - Indicates whether the activity is part of a journey or standalone.
 *
 * @returns {Object} An object containing the following properties:
 *   - activityText: A concatenated string of all formatted text lines for the activity.
 *     Examples:
 *     For a flight:
 *     ```
 *     Partial calculation failure
 *     Berlin (BER) --- Doha (DOH)*
 *     Doha (DOH) --- Johannesburg - Johannesburg Int'l (JNB)*
 *     *You can calculate the emissions of this section of the flight more precisely by adding further
 *     information such as airline or travel class.
 *     ```
 *     For a hotel:
 *     ```
 *     Sofitel Legend Old Cataract Aswan, Aswan, EGY
 *     5 *, 1 room, 1 night
 *     ```
 *   - textLineCount: An array where each element holds the number of rows needed to display
 *     information that semantically belongs in one row of the corresponding Activity.
 *     For example, if one leg of a flight spans two rows (even though semantically it is
 *     considered one row), the value at the position matching the leg number in the array
 *     will be 2.
 *   - totalTextLinesCount: The sum of all rows needed to display the Activity information.
 *     Note: Does not include the headline!
 */
export const setActivityText = (
    activity: AllActivityTypes,
    i18n: i18n,
    textBlockMaxWidth: number,
    fontFamily: PDFFont,
    fontSize: number,
    separator: string,
    isJourney: boolean,
) => {
    let totalTextLinesCount = 0
    let textResult: TextResultProps = []
    const {common: {activityType}} = activity
    switch (activityType) {
        case ACTIVITY_TYPE.flight:
            if (isActivityFlightState(activity)) {
                const {
                    flightRoute,
                    showFootnote,
                } = generateCustomerFlightRouteWithMetaDataString(activity, i18n)
                flightRoute?.forEach(flight => {
                    const countTextLines = addActivitySpecificTextToTextResults(
                        flight,
                        textBlockMaxWidth,
                        fontFamily,
                        fontSize,
                        separator,
                        textResult,
                    )
                    totalTextLinesCount += countTextLines
                })
                if (showFootnote) {
                    const countTextLines = addActivitySpecificTextToTextResults(
                        generatFlightCalculationImprovementsString(i18n),
                        textBlockMaxWidth,
                        fontFamily,
                        fontSize,
                        separator,
                        textResult,
                    )
                    totalTextLinesCount += countTextLines
                }
            }
            break;
        case ACTIVITY_TYPE.hotel:
            if (isActivityHotelState(activity)) {
                const {
                    locationString,
                    returnStayString
                } = generateCustomerHotelLocationString(activity, i18n, false, isJourney, WhereToDisplayActivityString.pdf)
                const countTextLines = addActivitySpecificTextToTextResults(
                    locationString,
                    textBlockMaxWidth,
                    fontFamily,
                    fontSize,
                    separator,
                    textResult,
                )
                totalTextLinesCount += countTextLines
                if (returnStayString) {
                    const countTextLines = addActivitySpecificTextToTextResults(
                        returnStayString,
                        textBlockMaxWidth,
                        fontFamily,
                        fontSize,
                        separator,
                        textResult,
                    )
                    totalTextLinesCount += countTextLines
                }
            }
            break;
        case ACTIVITY_TYPE.car_bus:
            if (isActivityCarBusState(activity)) {
                const {carBusString, showFootnote} = generateCustomerCarBusInformationString(activity, i18n, true)
                const countTextLines = addActivitySpecificTextToTextResults(
                    carBusString,
                    textBlockMaxWidth,
                    fontFamily,
                    fontSize,
                    separator,
                    textResult,
                )
                totalTextLinesCount += countTextLines
                if (showFootnote) {
                    const countTextLines = addActivitySpecificTextToTextResults(
                        generateCarBusCalculationImprovementsString(i18n),
                        textBlockMaxWidth,
                        fontFamily,
                        fontSize,
                        separator,
                        textResult,
                    )
                    totalTextLinesCount += countTextLines
                }
            }
            break;
        default:
            return activityType satisfies never
    }
    return {
        activityText: textResult.map(text => text.activityText).join(""),
        textLineCount: textResult.map(text => text.linesCount),
        totalTextLinesCount,
    }
}

/**
 * Generates CO2-related textual information and associated metrics for a given activity.
 *
 * @param {AllActivityTypes} activity - The activity for which the CO2 data is generated.
 * @param {AllActivityTypesArray} activities - An array containing all activity types.
 * @param {i18n} i18n - An internationalization instance for generating localized strings.
 * @param {PDFFont} fontFamily - The font family used for calculating text dimensions.
 * @param {number} fontSize - The font size used for calculating text dimensions.
 * @param {boolean} co2KgEWithReturnActivity - Indicates whether to include return activity CO2 in calculations.
 * @returns {Object} An object containing CO2-related text and metrics:
 * - `co2Text` {string[]} - An array of strings representing CO2 information per activity.
 * - `totalCo2KgEOfActivity` {number} - The total CO2 emitted for the specified activity.
 * - `widthOfLongestWord` {number} - The width, in units, of the longest word in the generated text based on the provided font and size.
 * - `numberOfLines` {number} - The total number of lines in the generated CO2 text.
 */
export const setBodyCo2 = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    i18n: i18n,
    fontFamily: PDFFont,
    fontSize: number,
    co2KgEWithReturnActivity: boolean,
) => {
    let resultText: string[] = []
    const {
        co2KgEPerPersonStrings,
        totalCo2KgEOfActivity
    } = generateCo2KgEForActivityString(activity, activities, co2KgEWithReturnActivity, i18n)
    co2KgEPerPersonStrings.forEach(co2 => resultText.push(co2))
    const longestWord = resultText.reduce((a, b) => a.length > b.length ? a : b)
    return {
        co2Text: resultText,
        totalCo2KgEOfActivity,
        widthOfLongestWord: fontFamily.widthOfTextAtSize(longestWord, fontSize),
        numberOfLines: resultText.length,
    }
}

/**
 * Calculates the height of a text block consisting of a title and a body.
 *
 * @param {number} totalTextLinesCount - The total number of text lines in the body.
 * @param {number} bodyLineHeight - The height of each line in the body text.
 * @param {number} titleFontSize - The font size of the title.
 * @param {number} marginFromTitleToBody - The vertical margin between the title and the body text.
 * @returns {Object} An object containing the total body height and the overall total height, with the following properties:
 *                   - totalBodyHeight: The calculated height of the body text based on the number of lines and line height.
 *                   - totalHeight: The combined height of the title, vertical margin, and total body text height.
 */
export const calculateHeightOfTitleAndBody = (
    totalTextLinesCount: number,
    bodyLineHeight: number,
    titleFontSize: number,
    marginFromTitleToBody: number
) => {
    const totalBodyHeight = totalTextLinesCount * bodyLineHeight
    const totalHeight = titleFontSize + marginFromTitleToBody + totalBodyHeight
    return {totalBodyHeight, totalHeight}
}

/**
 * Generates and returns the body text and associated metadata for a given activity with CO2 emissions and formatting details.
 * ...co2Text: which is something like (for a flight) [ "708 kg CO₂e p. P.", " --- " ] Why in an array? For a flight,
 *  each leg gets its own array entry (because each leg is displayed in a new row in the PDF).
 *  Like this, it is possible to i.e. map Co2 KgE to each leg.
 *  For a hotel it would look like this ["550 kg CO₂e p. P."] since a hotel stay is only "one stay" and is not spread across several rows
 *  Please refer to setActivityText() for the other variables
 *
 * @param {AllActivityTypes} activity - The specific activity for which the body text is to be generated.
 * @param {AllActivityTypesArray} activities - The array of all activity types, used for computations or context.
 * @param {i18n} i18n - Internationalization object for handling translations and localized content.
 * @param {PDFFont} fontFamily - The font family used for rendering text in the PDF document.
 * @param {number} fontSize - The size of the font used for text rendering.
 * @param {number} textBlockMaxWidth - The maximum allowable width for the text block.
 * @param {string} separator - The string used to separate sections or components in the text.
 * @param {boolean} isJourney - Indicates whether the activity is part of a journey or a standalone activity.
 * @param {boolean} co2KgEWithReturnActivity - Flag to determine whether CO2 emissions should include those of a return activity.
 * @returns {Object} An object containing the following properties:
 * - `co2Text` {string}: The calculated CO2 emissions text formatted for the activity.
 * - `bodyText` {string}: The main body text generated for the activity based on its details.
 * - `textLineCount` {number}: The number of lines used for the activity text.
 * - `totalTextLinesCount` {number}: The total number of text lines spanning the body and associated metadata.
 * - `totalCo2KgEOfActivity` {number}: The total CO2 emissions (in kilograms) for the specified activity.
 */
export const generateBodyText = (
    activity: AllActivityTypes,
    activities: AllActivityTypesArray,
    i18n: i18n,
    fontFamily: PDFFont,
    fontSize: number,
    textBlockMaxWidth: number,
    separator: string,
    isJourney: boolean,
    co2KgEWithReturnActivity: boolean,
) => {
    const {
        widthOfLongestWord,
        co2Text,
        totalCo2KgEOfActivity
    } = setBodyCo2(activity, activities, i18n, fontFamily, fontSize, co2KgEWithReturnActivity)
    const {activityText, textLineCount, totalTextLinesCount} = setActivityText(
        activity,
        i18n,
        textBlockMaxWidth - widthOfLongestWord,
        fontFamily,
        fontSize,
        separator,
        isJourney
    )
    return {co2Text, bodyText: activityText, textLineCount, totalTextLinesCount, totalCo2KgEOfActivity}
}

/**
 * Renders the total emissions text for an activity on a PDF document.
 *
 * This function calculates the position of the text based on the emission
 * calculation status, font size, and line height, then draws the specified
 * text on the given PDF page. The x-coordinate of the text is determined to
 * ensure the text aligns with the appropriate position considering page width
 * and margins.
 *
 * @param {AllActivityTypes} activity - An object containing information about the activity, including total emissions data.
 * @param {number} co2EBodyYPosition - The initial Y-axis position where the text should be rendered.
 * @param {PDFPage} page - The PDFPage instance where the text is drawn.
 * @param {number} bodyFontSize - Font size to use for the text.
 * @param {number} bodyLineHeight - Line height to be used for styling the text.
 * @param {number} pageWidth - The width of the PDF page, used to calculate text position.
 * @param {number} pageMarginRight - Right margin of the PDF page, used to align text.
 * @param {PDFFont} fontNormal - The font instance to apply when drawing the text.
 * @param {string} text - The content of the text to be rendered.
 */
export const drawActivityTotalEmissions = (
    activity: AllActivityTypes,
    co2EBodyYPosition: number,
    page: PDFPage,
    bodyFontSize: number,
    bodyLineHeight: number,
    pageWidth: number,
    pageMarginRight: number,
    fontNormal: PDFFont,
    text: string,
) => {
    const status = getBackendCalculationStatus(activity.common.totalEmissions)
    page.drawText(text, {
        x: pageWidth - pageMarginRight - fontNormal.widthOfTextAtSize(text, bodyFontSize),
        y: status === CALCULATE_EMISSIONS_RESPONSE_STATUS_TOTAL.success_with_errors ? co2EBodyYPosition - bodyLineHeight : co2EBodyYPosition,
        size: bodyFontSize,
        lineHeight: bodyLineHeight,
        font: fontNormal,
        color: black,
    })
}

/**
 * Draws the journey information on a PDF page, including the journey name and description.
 *
 * @param {string | null} name - The name of the journey. If null, it will not be drawn.
 * @param {string | null} description - A description of the journey. If null, it will not be drawn.
 * @param {any} customer - The customer data related to the journey.
 * @param {number} personCount - The number of people involved in the journey.
 * @param {number} journeyDuration - The duration of the journey.
 * @param {Object} i18n - Localization data for rendering text appropriately.
 * @param {PDFPage} page - The PDF page instance where the journey information is drawn.
 * @param {number} pageMarginLeft - Left margin of the page.
 * @param {number} pageMarginRight - Right margin of the page.
 * @param {number} bodyYPosition - The starting vertical position for the journey information.
 * @param {number} bodyFontSize - Font size for the body text.
 * @param {number} bodyLineHeight - Line height for the body text.
 * @param {number} titleFontSize - Font size for the title text.
 * @param {number} titleLineHeight - Line height for the title text.
 * @param {PDFFont} fontNormal - The standard font used for rendering text.
 * @param {PDFFont} fontBold - The bold font used for rendering the title.
 * @param {number} pageWidth - The width of the page.
 * @param {number} marginJourneyPropsToActivities - The margin height between the journey properties and subsequent activities.
 *
 * @returns {number} The total height of the drawn journey information plus any specified margin.
 */
export const drawJourneyInformation = (
    name: JourneyState['name'],
    description: JourneyState['description'],
    customer: JourneyState['customer'],
    personCount: JourneyState['personCount'],
    journeyDuration: JourneyState['journeyDuration'],
    i18n: i18n,
    page: PDFPage,
    pageMarginLeft: number,
    pageMarginRight: number,
    bodyYPosition: number,
    bodyFontSize: number,
    bodyLineHeight: number,
    titleFontSize: number,
    titleLineHeight: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    pageWidth: number,
    marginJourneyPropsToActivities: number,
): number => {
    const widthForText = pageWidth - pageMarginRight - pageMarginLeft
    let titleYPosition = bodyYPosition
    let descriptionYPosition = bodyYPosition
    let totalHeight = 0
    if (name !== null) {
        const {text, countTextLines} = wrapText(name.toString(), widthForText, fontBold, titleFontSize, " ")
        const titleHeight = (countTextLines * titleLineHeight) - titleFontSize
        let journeyTitle = text
        if (text.length >= MAX_JOURNEY_TITLE_LENGTH) journeyTitle = text.slice(0, MAX_JOURNEY_TITLE_LENGTH - JOURNEY_TITLE_DESCRIPTION_REPLACER.length) + JOURNEY_TITLE_DESCRIPTION_REPLACER
        journeyTitle = `${journeyTitle} (${personCount} ${generatePersonCountString(personCount, i18n)})`
        const textWidth = fontBold.widthOfTextAtSize(journeyTitle, titleFontSize)
        const xPosition = (pageWidth - textWidth) / 2
        page.drawText(journeyTitle, {
            x: xPosition,
            y: titleYPosition,
            size: titleFontSize,
            font: fontBold,
            lineHeight: titleLineHeight,
            color: black,
        })
        descriptionYPosition = titleYPosition - titleHeight
        totalHeight = totalHeight + titleHeight + titleLineHeight
    }

    if (description !== null) {
        const {text, countTextLines} = wrapText(description.toString(), widthForText, fontNormal, bodyFontSize, " ")
        const descriptionHeight = (countTextLines * bodyLineHeight) - bodyFontSize
        const marginToTitle = 10
        let descriptionText = text
        if (text.length >= MAX_JOURNEY_DESCRIPTION_LENGTH) descriptionText = text.slice(0, MAX_JOURNEY_DESCRIPTION_LENGTH - JOURNEY_TITLE_DESCRIPTION_REPLACER.length) + JOURNEY_TITLE_DESCRIPTION_REPLACER
        const textWidth = fontNormal.widthOfTextAtSize(descriptionText, bodyFontSize)
        const xPosition = (pageWidth - textWidth) / 2
        descriptionYPosition = descriptionYPosition - marginToTitle
        page.drawText(descriptionText, {
            x: xPosition,
            y: descriptionYPosition,
            size: bodyFontSize,
            font: fontNormal,
            lineHeight: bodyLineHeight,
            color: black,
        })
        descriptionYPosition += descriptionHeight
        totalHeight = totalHeight + marginToTitle + descriptionHeight + bodyLineHeight
    }

    return totalHeight + marginJourneyPropsToActivities
}

export const generateDateAndDayString = (journeyDuration: JourneyState['journeyDuration'], day: number, i18n: i18n) => {
    const {t, resolvedLanguage} = i18n
    const dayString = `${t('GENERAL.DAY')} ${day}`
    let dayAndDateString = dayString
    if (hasExactDates(journeyDuration)) {
        const {dates} = journeyDuration
        if (dates !== null) {
            const {startDate} = dates
            const dateFormattedString = formatDateString(startDate.plus({days: day - 1}), resolvedLanguage, false)
            dayAndDateString = `${dayString} (${dateFormattedString})`
        }
    }

    return dayAndDateString
}

/**
 * Draws a title row on a PDF page with the specified title and CO2 equivalent string.
 *
 * @param {i18n} i18n - The internationalization utility for creating localized strings.
 * @param {PDFPage} page - The PDF page where the row title will be drawn.
 * @param {number} pageMarginLeft - The left margin of the page.
 * @param {number} titleFontSize - The font size of the title text.
 * @param {number} titleYPosition - The Y position of the title text on the page.
 * @param {PDFFont} fontBold - The font to be used for rendering the title in bold.
 * @param {number} pageWidth - The total width of the page.
 * @param {number} pageMarginRight - The right margin of the page.
 * @param {PDFFont} fontNormal - The font to be used for rendering the CO2 equivalent string in normal style.
 * @param {string} title - The textual content of the row title.
 * @param {number} totalCo2KgE - The total CO2 equivalent value in kilograms.
 * @param {string | undefined} resolvedLanguage - The resolved language for localization, if available.
 */
export const drawRowTitle = (
    i18n: i18n,
    page: PDFPage,
    pageMarginLeft: number,
    titleFontSize: number,
    titleYPosition: number,
    fontBold: PDFFont,
    pageWidth: number,
    pageMarginRight: number,
    fontNormal: PDFFont,
    title: string,
    totalCo2KgE: number,
    resolvedLanguage: string | undefined,
) => {
    const {co2KgEString: co2KgEStringInTitleRow, co2KgETextWidth} = createTotalCo2String(
        i18n,
        totalCo2KgE!!,
        resolvedLanguage,
        fontNormal,
        titleFontSize,
        true
    )
    page.drawText(title, {
        x: pageMarginLeft,
        y: titleYPosition,
        size: titleFontSize,
        font: fontBold,
        color: black,
    })
    page.drawText(co2KgEStringInTitleRow, {
        x: pageWidth - pageMarginRight - co2KgETextWidth,
        y: titleYPosition,
        size: titleFontSize,
        font: fontNormal,
        color: black,
    })
}

/**
 * Draws a title and body text on a PDF page, with an additional CO2 emissions string
 * rendered alongside the title.
 *
 * @param {i18n} i18n - Internationalization object used for localizing text.
 * @param {PDFPage} page - The PDF page object where text will be drawn.
 * @param {number} pageMarginLeft - The left margin of the page, used for positioning text.
 * @param {number} bodyYPosition - The Y-coordinate where the body text starts.
 * @param {number} bodyFontSize - Font size used for the body text.
 * @param {number} titleFontSize - Font size used for the title text.
 * @param {number} bodyLineHeight - Line height for the body text.
 * @param {number} titleYPosition - The Y-coordinate where the title text is drawn.
 * @param {PDFFont} fontBold - Font used for rendering the title text in bold.
 * @param {number} pageWidth - The width of the PDF page.
 * @param {number} pageMarginRight - The right margin of the page, used for positioning text.
 * @param {PDFFont} fontNormal - Font used for rendering the body text and CO2 string in normal weight.
 * @param {string} title - The title text to draw on the page.
 * @param {string} bodyText - The body text to draw on the page.
 * @param {number} totalCo2KgE - Total CO2 emissions in kilograms equivalent, used for display.
 * @param {string|undefined} resolvedLanguage - The resolved language for localizing the CO2 string text.
 */
export const drawBodyTitleAndBody = (
    i18n: i18n,
    page: PDFPage,
    pageMarginLeft: number,
    bodyYPosition: number,
    bodyFontSize: number,
    titleFontSize: number,
    bodyLineHeight: number,
    titleYPosition: number,
    fontBold: PDFFont,
    pageWidth: number,
    pageMarginRight: number,
    fontNormal: PDFFont,
    title: string,
    bodyText: string,
    totalCo2KgE: number,
    resolvedLanguage: string | undefined,
) => {
    const {co2KgEString: co2KgEStringInTitleRow, co2KgETextWidth} = createTotalCo2String(
        i18n,
        totalCo2KgE!!,
        resolvedLanguage,
        fontNormal,
        titleFontSize,
        true
    )
    page.drawText(title, {
        x: pageMarginLeft,
        y: titleYPosition,
        size: titleFontSize,
        font: fontBold,
        color: black,
    })
    page.drawText(co2KgEStringInTitleRow, {
        x: pageWidth - pageMarginRight - co2KgETextWidth,
        y: titleYPosition,
        size: titleFontSize,
        font: fontNormal,
        color: black,
    })

    drawBody(
        page,
        pageMarginLeft,
        bodyYPosition,
        bodyFontSize,
        bodyLineHeight,
        fontNormal,
        bodyText,
    )
}

/**
 * Draws the main body text onto a PDF page at a specified position and with specific styles.
 *
 * @param {PDFPage} page - The PDFPage object where the body text will be drawn.
 * @param {number} pageMarginLeft - The left margin of the page where the body text starts.
 * @param {number} bodyYPosition - The Y-coordinate position where the body text will begin.
 * @param {number} bodyFontSize - The font size to be used for the body text.
 * @param {number} bodyLineHeight - The line height to be applied to the body text.
 * @param {PDFFont} fontNormal - The font to be used for the body text.
 * @param {string} text - The main body text to be drawn on the PDF page.
 * @returns {PDFPage} The modified PDFPage object with the body text drawn on it.
 */
export const drawBody = (
    page: PDFPage,
    pageMarginLeft: number,
    bodyYPosition: number,
    bodyFontSize: number,
    bodyLineHeight: number,
    fontNormal: PDFFont,
    text: string,
) => page.drawText(text, {
    x: pageMarginLeft,
    y: bodyYPosition,
    size: bodyFontSize,
    lineHeight: bodyLineHeight,
    font: fontNormal,
    color: black,
})

/**
 * Generates a formatted CO2 emissions string along with its text width and rounded value.
 *
 * @param {i18n} i18n - The internationalization instance used for localized text.
 * @param {number} totalCo2KgE - The total CO2 emissions in kilograms equivalent (kgE).
 * @param {string | undefined} resolvedLanguage - The resolved language code for formatting.
 * @param {PDFFont} fontNormal - The font used to calculate the text width.
 * @param {number} travelComponentTitleFontSize - The font size for the CO2 emissions string.
 * @param {boolean} addTotalString - Whether to prefix the string with a localized "TOTAL" label.
 * @param {additionalNote} - Additional note added to the total CO2 value.
 * @returns {Object} An object containing the formatted CO2 emissions string, its width in the given font and size, and its rounded value.
 * @property {string} co2KgEString - The formatted CO2 emissions string.
 * @property {number} co2KgETextWidth - The width of the CO2 emissions string in the provided font and size.
 * @property {number} roundedCo2KgE - The rounded value of the CO2 emissions.
 */
export const createTotalCo2String = (
    i18n: i18n,
    totalCo2KgE: number,
    resolvedLanguage: string | undefined,
    fontNormal: PDFFont,
    travelComponentTitleFontSize: number,
    addTotalString: boolean,
    additionalNote?: string,
): { co2KgEString: string, co2KgETextWidth: number, roundedCo2KgE: number } => {
    const {t} = i18n
    const totalString = t("GENERAL.TOTAL")
    let co2KgEString = `${formatCo2ETotalString(totalCo2KgE, resolvedLanguage)}`
    const roundedCo2KgE = roundCo2E(totalCo2KgE)
    if (addTotalString) co2KgEString = `${capitalizeFirstLetter(totalString)}: ${co2KgEString}`
    if (additionalNote) co2KgEString = `(${t(additionalNote)}) ${co2KgEString}`
    const co2KgETextWidth = fontNormal.widthOfTextAtSize(co2KgEString, travelComponentTitleFontSize)
    return {co2KgEString, co2KgETextWidth, roundedCo2KgE}
}

/**
 * Conditionally adds a new page to the travel components PDF document if there is not enough space
 * left on the current page to accommodate the footer and additional content. Updates layout values
 * for the new page and sets the header and footer.
 *
 * @param {number} heightLeftOver - The remaining height available on the current page.
 * @param {number} totalHeightOfBodyAndTitle - The combined height of the body content and title.
 * @param {number} footerHeight - The height of the footer to be added to the page.
 * @param {number} rowGap - The gap between rows, used to calculate spacing.
 * @param {PDFDocument} pdfDoc - The PDF document to which the pages belong.
 * @param {PDFPage} page - The current page object within the PDF document.
 * @param {number} pageHeight - The full height of a page.
 * @param {number} pageWidth - The width of a page.
 * @param {PDFFont} fontNormal - The normal font to be used for text in the document.
 * @param {PDFFont} fontBold - The bold font to be used for text in the document.
 * @param {number} pageMarginLeft - The left margin of the page.
 * @param {number} pageMarginRight - The right margin of the page.
 * @param {i18n} i18n - The i18n instance for localization.
 * @param {string} employeeName - The name of the employee added to the header section.
 * @param {string} agencyName - The name of the agency added to the header section.
 * @param {string} agencyAddress - The address of the agency added to the header section.
 * @param {LogoProps} logo - The logo properties used in the header.
 * @param {string} email - The email address displayed in the header.
 * @param {string} completePhoneNumber - The complete phone number displayed in the header.
 * @param {number} heightForContent - The total height available for content on a page.
 * @param {number} titleYPosition - The Y-coordinate of the title on the page.
 * @param {number} bodyYPosition - The Y-coordinate of the body content on the page.
 * @param {number} marginFromTitleToBody - The margin between the title and body content.
 * @param {number} beginningOfContent - The starting Y-coordinate for content on a new page.
 *
 * @returns {{heightLeftOver: number, titleYPosition: number, bodyYPosition: number, page: PDFPage}} An object containing the updated values for `heightLeftOver`,
 *                   `titleYPosition`, `bodyYPosition`, and the current `page` object.
 */
export const conditionallyAddPageToTravelComponentsPdf = (
    heightLeftOver: number,
    totalHeightOfBodyAndTitle: number,
    footerHeight: number,
    rowGap: number,
    pdfDoc: PDFDocument,
    page: PDFPage,
    pageHeight: number,
    pageWidth: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    pageMarginLeft: number,
    pageMarginRight: number,
    i18n: i18n,
    employeeName: string,
    agencyName: string,
    agencyAddress: string,
    logo: LogoProps,
    email: string,
    completePhoneNumber: string,
    heightForContent: number,
    titleYPosition: number,
    bodyYPosition: number,
    marginFromTitleToBody: number,
    beginningOfContent: number,
): { heightLeftOver: number, titleYPosition: number, bodyYPosition: number, page: PDFPage } => {
    if (heightLeftOver < footerHeight - rowGap) {
        page = pdfDoc.addPage()
        setHeader(
            page,
            pageHeight,
            pageWidth,
            fontNormal,
            fontBold,
            pageMarginLeft,
            pageMarginRight,
            i18n,
            pdfDoc,
            employeeName,
            agencyName,
            agencyAddress,
            logo,
            email,
            completePhoneNumber
        )
        setFooter(
            page,
            pageHeight,
            pageWidth,
            fontNormal,
            fontBold,
            footerHeight,
            pageMarginLeft,
            pageMarginRight,
            i18n,
        )
        heightLeftOver = heightForContent - totalHeightOfBodyAndTitle
        titleYPosition = beginningOfContent
        bodyYPosition = titleYPosition - marginFromTitleToBody
    }
    return {heightLeftOver, titleYPosition, bodyYPosition, page}
}

/**
 * Conditionally adds a new page to a PDF document if the remaining height on the current page
 * is insufficient for the footer. This function ensures proper layout continuity by managing
 * page headers, footers, and content positioning across pages.
 *
 * @param {number} heightLeftOver - The remaining height available on the current page.
 * @param {number} totalBodyHeight - The total height of the body content.
 * @param {number} footerHeight - The height of the footer.
 * @param {PDFDocument} pdfDoc - The PDF document instance.
 * @param {PDFPage} page - The current PDF page instance.
 * @param {number} pageHeight - The total height of a page.
 * @param {number} pageWidth - The total width of a page.
 * @param {PDFFont} fontNormal - The normal font used in the document.
 * @param {PDFFont} fontBold - The bold font used in the document.
 * @param {number} pageMarginLeft - The left margin of the page.
 * @param {number} pageMarginRight - The right margin of the page.
 * @param {i18n} i18n - The internationalization instance for text and formatting.
 * @param {string} employeeName - The name of the employee.
 * @param {string} agencyName - The name of the agency.
 * @param {string} agencyAddress - The address of the agency.
 * @param {LogoProps} logo - The logo properties of the agency.
 * @param {string} email - The email address to be displayed.
 * @param {string} completePhoneNumber - The complete phone number to be displayed.
 * @param {number} heightForContent - The total height available for content within a page.
 * @param {number} activitiesYPosition - The Y-position for activities on the page.
 * @param {number} beginningOfContent - The Y-position where the main content starts.
 * @returns {Object} Returns an object containing the updated heightLeftOver, the new or updated page,
 * the updated activitiesYPosition, and a boolean value indicating if a new page was added.
 */
export const conditionallyAddPageToJourneyPdf = (
    heightLeftOver: number,
    totalBodyHeight: number,
    footerHeight: number,
    pdfDoc: PDFDocument,
    page: PDFPage,
    pageHeight: number,
    pageWidth: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    pageMarginLeft: number,
    pageMarginRight: number,
    i18n: i18n,
    employeeName: string,
    agencyName: string,
    agencyAddress: string,
    logo: LogoProps,
    email: string,
    completePhoneNumber: string,
    heightForContent: number,
    activitiesYPosition: number,
    beginningOfContent: number,
) => {
    let pageAdded = false
    if (heightLeftOver < footerHeight) {
        page = pdfDoc.addPage()
        pageAdded = true
        setHeader(
            page,
            pageHeight,
            pageWidth,
            fontNormal,
            fontBold,
            pageMarginLeft,
            pageMarginRight,
            i18n,
            pdfDoc,
            employeeName,
            agencyName,
            agencyAddress,
            logo,
            email,
            completePhoneNumber
        )
        setFooter(
            page,
            pageHeight,
            pageWidth,
            fontNormal,
            fontBold,
            footerHeight,
            pageMarginLeft,
            pageMarginRight,
            i18n,
        )
        heightLeftOver = heightForContent - totalBodyHeight
        activitiesYPosition = beginningOfContent
    }
    return {heightLeftOver, page, activitiesYPosition, pageAdded}
}

/**
 * Calculates the starting position of the content by subtracting the header height from the total page height.
 *
 * @param {number} pageHeight - The total height of the page.
 * @param {number} headerHeight - The height of the header section.
 * @returns {number} The numerical value representing the starting position of the content.
 */
export const calculateBeginningOfContent = (
    pageHeight: number,
    headerHeight: number
): number => pageHeight - headerHeight


/**
 * Generates and draws an emissions summary on a PDF document.
 * The summary can be per activity or per travel option, as specified by the `whichSummary` parameter.
 *
 * @param {AllActivityTypesArray} activities - Array of activities or travel options to summarize emissions for.
 * @param {number} pageWidth - The width of the current PDF page.
 * @param {number} bodyLineHeight - The line height for the body text.
 * @param {number} bodyFontSize - The font size for the body text.
 * @param {number} titleFontSize - The font size for the title text.
 * @param {number} marginFromBodyToTitle - The margin between the body and the title in the summary.
 * @param {PDFDocument} pdfDoc - Reference to the PDF document being created or modified.
 * @param {number} heightLeftOver - Available height left on the current page for drawing content.
 * @param {number} footerHeight - Height of the footer on the page.
 * @param {number} rowGap - The vertical gap between rows in the emission summary.
 * @param {PDFPage} page - The current page of the PDF document where the summary is being drawn.
 * @param {number} height - Total available height for content on the PDF page.
 * @param {PDFFont} fontNormal - The normal font used for text.
 * @param {PDFFont} fontBold - The bold font used for titles or emphasized text.
 * @param {number} pageMarginLeft - The left margin of the page.
 * @param {number} pageMarginRight - The right margin of the page.
 * @param {i18n} i18n - The i18n instance for translations and localization.
 * @param {string} userName - The name of the user to be included in the summary.
 * @param {string} agencyName - The name of the agency to be included in the summary header.
 * @param {string} agencyAddress - The address of the agency to be included in the summary header.
 * @param {LogoProps} logo - Properties and configuration for the agency's logo in the summary.
 * @param {string} email - Email address of the agency or responsible party.
 * @param {string} completePhoneNumber - Complete phone number including country code for the agency or user.
 * @param {number} heightForContent - Total available height for content within the document layout.
 * @param {number} titleYPosition - The Y-axis position for the title of the summary.
 * @param {number} bodyYPosition - The Y-axis position for the body text in the summary.
 * @param {number} originalTitleYPosition - The original Y-axis position of the title text before adjustments.
 * @param {'perActivity' | 'perTravelOption'} whichSummary - Specifies the type of summary to generate ("perActivity" or "perTravelOption").
 *
 * @param journeyDuration - Duration of the journey.
 * @returns {void} This function does not return a value but instead modifies the provided PDF document to include the emissions summary.
 */
export const drawEmissionsSummary = (
    activities: AllActivityTypesArray,
    pageWidth: number,
    bodyLineHeight: number,
    bodyFontSize: number,
    titleFontSize: number,
    marginFromBodyToTitle: number,
    pdfDoc: PDFDocument,
    heightLeftOver: number,
    footerHeight: number,
    rowGap: number,
    page: PDFPage,
    height: number,
    fontNormal: PDFFont,
    fontBold: PDFFont,
    pageMarginLeft: number,
    pageMarginRight: number,
    i18n: i18n,
    userName: string,
    agencyName: string,
    agencyAddress: string,
    logo: LogoProps,
    email: string,
    completePhoneNumber: string,
    heightForContent: number,
    titleYPosition: number,
    bodyYPosition: number,
    originalTitleYPosition: number,
    whichSummary: 'perActivity' | 'perTravelOption',
    journeyDuration?: JourneyState['journeyDuration'],
): void => {
    const {t, resolvedLanguage} = i18n
    const textLinesCountPerActivitySummary = 1
    const textLinesCountForTotalSummary = 1
    let co2KgEPerActivity: Co2KgEPerActivityProps | Co2KgEPerTravelOptionProps
    if (whichSummary === "perActivity") {
        co2KgEPerActivity = getCo2KgEForActivityType(activities, true)
    } else {
        co2KgEPerActivity = getCo2KgEForAllTravelOptionTypes(activities, true)
    }
    const totalActivitySummaryTextLinesCount = Object.keys(co2KgEPerActivity).length
    const lineXPosition = 50;
    const lineStart = pageWidth - lineXPosition
    const lineThickness = 1.5
    const marginBottomLine = 15
    let differentValueRoundedString: string | undefined
    let offsetTotalCo2KgESummaryToLastActivity = 20
    let perActivitySummaryBodyHeight
    let totalPerActivitySummaryHeight
    let summaryAllActivitiesBodyHeight
    let totalSummaryAllActivitiesHeight
    perActivitySummaryBodyHeight = (totalActivitySummaryTextLinesCount * bodyLineHeight) - bodyFontSize
    totalPerActivitySummaryHeight = titleFontSize + marginFromBodyToTitle + perActivitySummaryBodyHeight
    summaryAllActivitiesBodyHeight = (textLinesCountForTotalSummary * bodyLineHeight) - bodyFontSize
    totalSummaryAllActivitiesHeight = lineThickness + marginBottomLine + summaryAllActivitiesBodyHeight
    const completeHeightSummary = totalSummaryAllActivitiesHeight + totalPerActivitySummaryHeight + offsetTotalCo2KgESummaryToLastActivity
    const previousPageCount = pdfDoc.getPageCount()
    heightLeftOver -= completeHeightSummary
    const result = conditionallyAddPageToTravelComponentsPdf(
        heightLeftOver,
        perActivitySummaryBodyHeight,
        footerHeight,
        rowGap,
        pdfDoc,
        page,
        height,
        pageWidth,
        fontNormal,
        fontBold,
        pageMarginLeft,
        pageMarginRight,
        i18n,
        userName,
        agencyName,
        agencyAddress,
        logo,
        email,
        completePhoneNumber,
        heightForContent,
        titleYPosition,
        bodyYPosition,
        marginFromBodyToTitle,
        originalTitleYPosition,
    )
    const currentPageCount = pdfDoc.getPageCount()
    if (currentPageCount !== previousPageCount) offsetTotalCo2KgESummaryToLastActivity = 0
    titleYPosition = result.titleYPosition - offsetTotalCo2KgESummaryToLastActivity
    bodyYPosition = result.bodyYPosition - offsetTotalCo2KgESummaryToLastActivity
    page = result.page;
    page.drawText(i18n.t("GENERAL.TOTAL_EMISSIONS"), {
        x: pageMarginLeft,
        y: titleYPosition,
        size: titleFontSize,
        font: fontBold,
        color: black,
    });
    let totalCo2KgEAllActivitiesRounded = 0
    Object.entries(co2KgEPerActivity).forEach(([key, value]) => {
        let emissionTypeString: string | null = null
        let co2KgE: number | null = null

        if (whichSummary === "perActivity") {
            if (Object.values(ACTIVITY_TYPE).includes(key as ACTIVITY_TYPE)) {
                emissionTypeString = t(`ACTIVITIES.${key.toUpperCase()}.TITLE`);
                co2KgE = value as number;
            }
        } else {
            if (Object.values(TRAVEL_OPTION_TYPE).includes(key as TRAVEL_OPTION_TYPE)) {
                emissionTypeString = t(`GENERAL.${key.toUpperCase()}`);
                co2KgE = value as number
            }
        }
        if (co2KgE !== null && emissionTypeString !== null) {
            const {co2KgEString, co2KgETextWidth, roundedCo2KgE} = createTotalCo2String(
                i18n,
                co2KgE,
                resolvedLanguage,
                fontNormal,
                bodyFontSize,
                false
            )
            page.drawText(emissionTypeString, {
                x: pageMarginLeft,
                y: bodyYPosition,
                size: bodyFontSize,
                font: fontNormal,
                color: black,
            })
            page.drawText(co2KgEString, {
                x: pageWidth - pageMarginRight - co2KgETextWidth,
                y: bodyYPosition,
                size: bodyFontSize,
                font: fontNormal,
                color: black,
            })
            bodyYPosition = bodyYPosition - textLinesCountPerActivitySummary * bodyLineHeight
            totalCo2KgEAllActivitiesRounded += roundedCo2KgE
        }
    })

    const lineYPosition = bodyYPosition
    page.drawLine({
        start: {
            x: lineXPosition,
            y: lineYPosition
        },
        end: {
            x: lineStart,
            y: lineYPosition
        },
        thickness: lineThickness,
        color: darkGrey,
    })

    if (journeyDuration) {
        const totalJourneyEmissions = getTotalCo2KgEForJourneyViaSumOfDays(activities, journeyDuration, true)
        if (totalJourneyEmissions !== totalCo2KgEAllActivitiesRounded) differentValueRoundedString = "HINTS.COMMON.ROUNDED_VALUES"
    }

    const {co2KgEString, co2KgETextWidth} = createTotalCo2String(
        i18n,
        totalCo2KgEAllActivitiesRounded,
        resolvedLanguage,
        fontNormal,
        bodyFontSize,
        true,
        differentValueRoundedString
    )
    page.drawText(co2KgEString, {
        x: pageWidth - pageMarginRight - co2KgETextWidth,
        y: lineYPosition - marginBottomLine,
        size: bodyFontSize,
        font: fontNormal,
        color: black,
    })
}